import VariablePicker from '@/components/nodes/settings/variable/VariablePicker';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { VariableRef } from '@/types/variable';
import { isVariable } from '@/util/typeGuards';
import { textblockUtil } from '@/util/validation/textblock';
import { valueUtil } from '@/util/validation/value';
import { variableUtil } from '@/util/validation/variable';
import { Button, Stack } from '@mui/material';
import { FC, useState } from 'react';
import { ParamProps } from './ParamRenderer';
import ParamTextField from './ParamTextField';
import VariableView from './VariableView';

const ParamStringInteger: FC<ParamProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();

  const node = application.blocks.nodes[props.nodeId];
  const blockName = `${props.nodeId}: ${node.type}${node.label && ` (${node.label})`}`;

  const errorArgs: [Application, Metadata, number, number, string, Configuration, Detail] = [
    application,
    metadata,
    props.parentId,
    props.nodeId,
    blockName,
    props.configuration,
    props.initialValue as Detail
  ];

  const initialValue = valueUtil().getValue(props.configuration, props.initialValue);
  const isVar = isVariable(initialValue);

  const [openVarPicker, setOpenVarPicker] = useState(false);

  const handleButtonClick = () => {
    if (isVar) {
      //Is variable. Button should toggle to ParamTextField
      props.onChange([props.configuration.value, '']);
    } else {
      //Is textfield. Open variable picker!
      setOpenVarPicker(true);
    }
  };

  const varType = props.configuration.subtype || props.configuration.type;

  return (
    <Stack
      direction='row'
      width='100%'
      gap={2}
      alignItems='center'
      justifyContent='space-between'
      overflow='auto'
      p={1}
    >
      {isVar ? (
        <VariableView
          required={props.configuration.required}
          label={props.configuration.label}
          subroutineId={props.parentId}
          type={varType}
          valueKey={props.configuration.value}
          initialValue={initialValue}
          onChange={(newVariable) => props.onChange([props.configuration.value, newVariable])}
          errorSeverity={variableUtil().validate(...errorArgs)[0]?.severity}
        />
      ) : (
        <ParamTextField
          nodeId={props.nodeId}
          parentId={props.parentId}
          configuration={props.configuration}
          initialValue={initialValue}
          onChange={props.onChange}
          errors={textblockUtil().validate(...errorArgs)}
        />
      )}
      <Button variant='contained' color='secondary' onClick={handleButtonClick} sx={{ flexGrow: 0.25 }}>
        {isVar ? 'Value' : 'Var'}
      </Button>
      <VariablePicker
        subroutineId={props.parentId}
        open={openVarPicker}
        onClose={() => setOpenVarPicker(false)}
        onVariablePicked={(newVariable: VariableRef) => {
          props.onChange([props.configuration.value, newVariable]);
        }}
        type={[varType]}
      />
    </Stack>
  );
};

export default ParamStringInteger;
